import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { designSystem } from '@yola/ws-ui';
import { integration, i18next, view } from '@yola/ws-sdk';
import helpCenter from 'yola-editor/src/js/modules/help-center';
import segment from 'src/js/modules/analytics/segment';
import publishing from '../../publishing';
import actions from '../actions';
import PreviewModeLayout from '../components/preview-mode-layout';
import PreviewViewport from '../components/preview-viewport';
import getPreviewModeForDevice from '../helpers/get-preview-mode-for-device';
import adaptFullscreenForMode from '../helpers/adapt-fullscreen-for-mode';

const {
  trackers: { trackEvent },
  constants: { events },
} = segment;

const { PreviewModeHeader } = designSystem;

const getCaptions = () => ({
  publish: i18next.t('Publish'),
  back: i18next.t('Back to editing'),
});

const PreviewContainer = ({ origin, path = '/' }) => {
  const deviceMode = useSelector(view.selectors.getDeviceMode);
  const [mode, setMode] = useState(() => getPreviewModeForDevice(deviceMode));
  const dispatch = useDispatch();
  const captions = useMemo(() => getCaptions(), []);
  const { userData = {} } = useSelector(integration.selectors.getSettings);

  const { is_white_label: isWL } = userData;
  const iframeWindow = useRef(null);

  const hideZendeskWidget = useCallback(() => {
    if (isWL) return;

    helpCenter.operations.hideForm();
  }, [isWL]);

  const showZendeskWidget = useCallback(() => {
    if (isWL) return;

    helpCenter.operations.showForm();
  }, [isWL]);

  const handleDocumentLoad = (iframe) => {
    const { contentWindow } = iframe;
    iframeWindow.current = contentWindow;

    contentWindow.postMessage(JSON.stringify({ event: 'previewmode' }), origin);
  };

  const handleModeChange = (newMode) => {
    setMode(newMode);
    trackEvent(events.PREVIEW_DEVICE_CLICKED, {
      typeId: newMode,
    });

    if (iframeWindow.current) {
      adaptFullscreenForMode(newMode, iframeWindow.current, origin);
    }
  };

  const handleBackClick = () => {
    dispatch(actions.hide());
    trackEvent(events.PREVIEW_BACK_CLICKED);
    showZendeskWidget();
  };

  const handlePublish = () => {
    dispatch(actions.hide());

    dispatch(publishing.actions.initiatePublishWebsite());

    trackEvent(events.PUBLISH_TRIGGER_CLICKED, {
      triggerId: 'preview-header',
    });
    showZendeskWidget();
  };

  useEffect(() => {
    hideZendeskWidget();
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, []);

  const previewSrc = `${origin}${path}`;

  return (
    <PreviewModeLayout>
      <div className="ws-preview-mode-header">
        <PreviewModeHeader
          onBack={handleBackClick}
          onPublish={handlePublish}
          onToggleMode={handleModeChange}
          selectedMode={mode}
          captions={captions}
        />
      </div>
      <PreviewModeLayout.Body>
        <PreviewViewport mode={mode} src={previewSrc} onDocumentLoad={handleDocumentLoad} />
      </PreviewModeLayout.Body>
    </PreviewModeLayout>
  );
};

PreviewContainer.propTypes = {
  origin: PropTypes.string.isRequired,
  path: PropTypes.string,
};

export default PreviewContainer;
